import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eraser, Save, Download, ArrowLeft } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import { useApi } from '../contexts/ApiContext';
import { useAIOperation } from '../hooks/useAIOperation';
import { ProviderRegistry } from '../services/providerRegistry';
import { ReplicateProvider } from '../services/providers/ReplicateProvider';
import ImagePicker from '../components/ImagePicker';
import ProviderToggle from '../components/ProviderToggle';
import ImageCompare from '../components/ImageCompare';
import JobStatus, { CostBadge } from '../components/JobStatus';
import MaskPainter from '../components/MaskPainter';
import type { MedaImage } from '../types/image';

export default function RemovePage() {
  const navigate = useNavigate();
  const { addImage } = useLibrary();
  const { tier, setTier, apiKeys } = useApi();
  const [opState, opActions] = useAIOperation();

  const [selectedImage, setSelectedImage] = useState<MedaImage | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [maskBlob, setMaskBlob] = useState<Blob | null>(null);
  const [prompt, setPrompt] = useState('');

  const registry = useMemo(() => new ProviderRegistry({
    huggingfaceKey: apiKeys.huggingface,
    replicateKey: apiKeys.replicate,
    preferredTier: tier,
  }), [apiKeys.huggingface, apiKeys.replicate, tier]);

  const provider = useMemo(() => registry.getBestProvider('inpaint'), [registry]);
  const providers = useMemo(() => registry.getProvidersForOp('inpaint'), [registry]);
  const hasFree = providers.some(p => p.info.tier === 'free');
  const hasPaid = providers.some(p => p.info.tier === 'paid');

  const estimatedCost = useMemo(() => {
    if (!provider) return 0;
    if (provider.info.tier === 'free') return 0;
    if (provider instanceof ReplicateProvider) return provider.getEstimatedCost('inpaint');
    return 0.03;
  }, [provider]);

  useEffect(() => () => {
    if (originalUrl) URL.revokeObjectURL(originalUrl);
  }, [originalUrl]);

  const handleSelect = useCallback((image: MedaImage) => {
    setSelectedImage(image);
    if (originalUrl) URL.revokeObjectURL(originalUrl);
    setOriginalUrl(URL.createObjectURL(image.blob));
    setMaskBlob(null);
    opActions.reset();
  }, [originalUrl, opActions]);

  const handleUploadFile = useCallback(async (file: File) => {
    const img = await addImage(file, file.name);
    handleSelect(img);
  }, [addImage, handleSelect]);

  const handleRun = useCallback(async () => {
    if (!selectedImage || !provider || !maskBlob) return;
    await opActions.run(provider, 'inpaint', selectedImage.blob, {
      mask: maskBlob,
      prompt: prompt.trim() || 'seamless clean background, high quality',
    });
  }, [selectedImage, provider, maskBlob, prompt, opActions]);

  const handleSave = useCallback(async () => {
    if (!opState.resultBlob || !selectedImage) return;
    const label = prompt.trim() ? `${selectedImage.name} (inpaint: ${prompt.trim().slice(0, 24)})` : `${selectedImage.name} (inpaint)`;
    await addImage(
      opState.resultBlob,
      label,
      'generated',
      selectedImage.id,
      'inpaint',
    );
    navigate('/library');
  }, [opState.resultBlob, selectedImage, prompt, addImage, navigate]);

  const handleDownload = useCallback(() => {
    if (!opState.resultUrl || !selectedImage) return;
    const a = document.createElement('a');
    a.href = opState.resultUrl;
    a.download = `${selectedImage.name}_inpaint.png`;
    a.click();
  }, [opState.resultUrl, selectedImage]);

  const noProvider = !provider;
  const needsKey = noProvider && !apiKeys.replicate;
  const canRun = !!(selectedImage && provider && maskBlob);
  const running = opState.status === 'submitting' || opState.status === 'processing';

  return (
    <div className="flex flex-col h-full sage-animate-fade-in">
      <div
        className="flex items-center gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
      >
        <button onClick={() => navigate('/')} className="p-1.5 rounded-lg" style={{ color: 'var(--sage-text-muted)' }}>
          <ArrowLeft size={18} />
        </button>
        <Eraser size={18} style={{ color: 'var(--sage-primary)' }} />
        <h1 className="text-sm font-medium flex-1">Remove / Inpaint</h1>
        <ProviderToggle tier={tier} onToggle={setTier} freeAvailable={hasFree} paidAvailable={hasPaid} />
      </div>

      <div className="flex flex-1 min-h-0">
        <div
          className="w-72 p-4 border-r overflow-y-auto flex flex-col gap-5"
          style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
              Select Image
            </p>
            <ImagePicker
              selectedId={selectedImage?.id ?? null}
              onSelect={handleSelect}
              onUpload={handleUploadFile}
            />
          </div>

          {selectedImage && (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--sage-text-muted)' }}>
                  Fill Prompt (optional)
                </p>
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="e.g. clean beach sand, no people"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-xs resize-none outline-none"
                  style={{
                    backgroundColor: 'var(--sage-bg)',
                    color: 'var(--sage-text)',
                    border: '1px solid var(--sage-white-10)',
                  }}
                />
                <p className="text-[10px] mt-1" style={{ color: 'var(--sage-text-muted)' }}>
                  Empty = remove & fill with surrounding context.
                </p>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--sage-text-muted)' }}>
                  Provider
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: 'var(--sage-text)' }}>
                    {provider?.info.name || 'None configured'}
                  </span>
                  <CostBadge cost={estimatedCost} tier={provider?.info.tier || 'paid'} />
                </div>
                {!hasFree && (
                  <p className="text-[10px] mt-1" style={{ color: 'var(--sage-text-muted)' }}>
                    Inpaint requires a paid provider (Replicate).
                  </p>
                )}
              </div>

              {needsKey ? (
                <div
                  className="p-3 rounded-lg text-xs"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--sage-accent) 10%, var(--sage-bg))', color: 'var(--sage-accent)' }}
                >
                  Add a Replicate API key in Settings to enable inpaint.
                  <button onClick={() => navigate('/settings')} className="underline ml-1">Open Settings</button>
                </div>
              ) : (
                <button
                  onClick={handleRun}
                  disabled={!canRun || running}
                  className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                >
                  {running ? 'Processing...' : maskBlob ? 'Remove Masked Area' : 'Paint mask to continue'}
                </button>
              )}

              <JobStatus
                status={opState.status}
                progress={opState.progress}
                error={opState.error}
                onRetry={handleRun}
              />

              {opState.status === 'succeeded' && (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium"
                    style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
                  >
                    <Save size={14} /> Save to Library
                  </button>
                  <button
                    onClick={handleDownload}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs"
                    style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text-muted)', border: '1px solid var(--sage-white-10)' }}
                  >
                    <Download size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex-1 flex items-center justify-center p-6 min-w-0 overflow-auto">
          {!selectedImage ? (
            <div className="text-center">
              <Eraser size={48} style={{ color: 'var(--sage-white-10)' }} className="mx-auto mb-3" />
              <p className="text-sm" style={{ color: 'var(--sage-text-muted)' }}>
                Select an image to start painting a mask
              </p>
            </div>
          ) : opState.status === 'succeeded' && opState.resultUrl && originalUrl ? (
            <div className="w-full max-w-3xl">
              <ImageCompare
                beforeUrl={originalUrl}
                afterUrl={opState.resultUrl}
                beforeLabel="Original"
                afterLabel="Inpainted"
              />
            </div>
          ) : originalUrl ? (
            <div className="w-full max-w-3xl">
              <MaskPainter imageUrl={originalUrl} onMaskChange={setMaskBlob} />
              <p className="text-[11px] text-center mt-3" style={{ color: 'var(--sage-text-muted)' }}>
                Paint over what you want removed. The pink overlay shows the mask area.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
